/**
 * Guide API Client — logs interactions, sessions, songs to guide-api.
 *
 * Uses service-to-service trust: X-API-KEY header + user_id from local
 * user info (no JWT required). The browser is just a relay for content
 * that the Guide bot generates server-side, so we don't gate this on
 * the user's auth token lifetime.
 *
 * Falls back to JWT if user_id is missing (anonymous mode not supported).
 */

import { getUser } from './auth';

const GUIDE_API = 'https://guide-api.arkserver.arkturian.com/api/v1';
export const GUIDE_API_BASE = GUIDE_API;
const SERVICE_KEY = 'Inetpass1';
/** guide-api service-to-service key (X-API-KEY). Same value authFetch uses. */
export const GUIDE_API_KEY = SERVICE_KEY;

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const user = getUser();
  if (!user?.id) throw new Error('No user_id — login required at least once');

  // Inject user_id into URL if not already present
  const sep = path.includes('?') ? '&' : '?';
  const url = path.includes('user_id=')
    ? path
    : `${path}${sep}user_id=${encodeURIComponent(user.id)}&user_email=${encodeURIComponent(user.email || '')}`;

  return fetch(`${GUIDE_API}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-API-KEY': SERVICE_KEY,
      ...(options.headers || {}),
    },
  });
}

/** Create a guide session — returns session_id */
export async function createSession(params: {
  city?: string;
  country?: string;
  transport?: string;
  theme?: string;
  pool_instance?: string;
}): Promise<number | null> {
  const query = new URLSearchParams(params as Record<string, string>).toString();
  const res = await authFetch(`/sessions?${query}`, { method: 'POST' });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`createSession HTTP ${res.status}: ${errText.slice(0, 100)}`);
  }
  const data = await res.json();
  return data.id ?? null;
}

/** Log an interaction — returns interaction_id */
export async function logInteraction(params: {
  session_id: number;
  topic_title?: string;
  topic_wikipedia?: string;
  city?: string;
  lat?: number;
  lon?: number;
  content?: string;
  vibe?: string;
  language?: string;
  theme?: string;
  /** World-Korpus Phase 1: im generischen Modus zusätzlich als narration_point
   *  auf den Welt-Track spiegeln (guide-api macht Radius-Dedupe). */
  world_corpus?: boolean;
}): Promise<number | null> {
  try {
    const query = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])
      )
    ).toString();
    const res = await authFetch(`/interactions?${query}`, { method: 'POST' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.id ?? null;
  } catch {
    return null;
  }
}

/** Attach audio storage ID to an interaction */
export async function attachAudio(interactionId: number, audioStorageId: number): Promise<boolean> {
  try {
    const res = await authFetch(`/interactions/${interactionId}?audio_storage_id=${audioStorageId}`, { method: 'PATCH' });
    return res.ok;
  } catch {
    return false;
  }
}

/** Attach topic image URL to an interaction */
export async function attachImage(interactionId: number, imageUrl: string): Promise<boolean> {
  try {
    const res = await authFetch(`/interactions/${interactionId}?topic_image_url=${encodeURIComponent(imageUrl)}&topic_image_shown=true`, { method: 'PATCH' });
    return res.ok;
  } catch {
    return false;
  }
}

/** Log a song played */
export async function logSong(params: {
  session_id: number;
  query?: string;
  title?: string;
  storage_url?: string;
}): Promise<boolean> {
  try {
    const query = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    ).toString();
    const res = await authFetch(`/songs?${query}`, { method: 'POST' });
    return res.ok;
  } catch {
    return false;
  }
}

/** End a session */
export async function endSession(sessionId: number): Promise<boolean> {
  try {
    const res = await authFetch(`/sessions/${sessionId}/end`, { method: 'POST' });
    return res.ok;
  } catch {
    return false;
  }
}

/** Get the best nearby interaction — shown during warmup (public, no auth) */
export async function getNearbyInteraction(lat?: number, lon?: number, lang: string = 'de', theme?: string): Promise<{
  topic_title: string;
  content: string;
  city: string;
  vibe: string;
  topic_image_url: string | null;
  topic_wikipedia: string | null;
  audio_url: string | null;
  lat: number;
  lon: number;
  theme?: string;
  timestamp: string;
} | null> {
  try {
    const params = new URLSearchParams();
    if (lat !== undefined && lon !== undefined) {
      params.set('lat', lat.toFixed(5));
      params.set('lon', lon.toFixed(5));
    }
    params.set('lang', lang);
    if (theme) params.set('theme', theme);
    const res = await fetch(`${GUIDE_API}/interactions/nearby?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data || null;
  } catch {
    return null;
  }
}

/** Find a reviewed image for a topic via title similarity (public, no auth) */
export async function findReviewedImage(title: string, lat?: number, lon?: number): Promise<string | null> {
  try {
    const params = new URLSearchParams({ title });
    if (lat !== undefined && lon !== undefined) {
      params.set('lat', lat.toFixed(5));
      params.set('lon', lon.toFixed(5));
    }
    const res = await fetch(`${GUIDE_API}/interactions/find-image?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.image || null;
  } catch {
    return null;
  }
}

/** Get compact list of topics already heard near a location */
export async function getHeardNearby(lat: number, lon: number): Promise<string> {
  const res = await fetch(`${GUIDE_API}/interactions/heard?lat=${lat.toFixed(5)}&lon=${lon.toFixed(5)}`);
  if (!res.ok) throw new Error(`heard HTTP ${res.status}`);
  const data = await res.json();
  return data.heard || '';
}

/** Trigger async AI review for an interaction (fire-and-forget, no auth needed) */
export function triggerReview(interactionId: number): void {
  fetch(`${GUIDE_API}/interactions/${interactionId}/review`, { method: 'POST' }).catch(() => {});
}

/** Pick one random city from the GeoNames pool (~169k populated places
 *  worldwide). Returns null on error so the caller can fall back to
 *  client-side random. */
export async function getRandomCity(minPopulation: number = 1000): Promise<{
  name: string; country_code: string; lat: number; lon: number; population: number;
} | null> {
  try {
    const res = await fetch(`${GUIDE_API}/cities/random?min_population=${minPopulation}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.error || typeof data?.lat !== 'number') return null;
    return data;
  } catch {
    return null;
  }
}

/** Live image search — Commons + DuckDuckGo without AI review (~3-5s).
 * Used in enrichment chain when Wikipedia + cache miss, before Places fallback. */
export async function findImageFast(title: string, city: string = ''): Promise<{ image: string | null; source: string | null }> {
  try {
    const params = new URLSearchParams({ title });
    if (city) params.set('city', city);
    const res = await fetch(`${GUIDE_API}/find-image-fast?${params}`);
    if (!res.ok) return { image: null, source: null };
    return await res.json();
  } catch {
    return { image: null, source: null };
  }
}

/** Get city history (topics already heard) */
export async function getCityHistory(city: string): Promise<string[]> {
  try {
    const res = await authFetch(`/history/${encodeURIComponent(city)}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.topics_heard || [];
  } catch {
    return [];
  }
}
